import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, ArrowLeft, Home, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ListingCard from '@/components/ListingCard';

interface WishlistItem {
  id: string;
  created_at: string;
  listing: {
    id: string;
    title: string;
    price_egp: number;
    deposit_egp: number;
    photos: string[];
    furnished: boolean;
    verified: boolean;
    unit_type: string;
    gender_rules: string;
    address: string;
    distance_km: number;
    university: {
      name_en: string;
      name_ar: string;
    };
    owner: {
      full_name: string;
    };
  };
}

const Wishlist = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);

  const handleBackClick = () => {
    navigate(-1);
  };

  const fetchWishlistItems = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select(`
          id,
          created_at,
          listing:listings (
            id,
            title,
            price_egp,
            deposit_egp,
            photos,
            furnished,
            verified,
            unit_type,
            gender_rules,
            address,
            distance_km,
            owner_id,
            university:universities (
              name_en,
              name_ar
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching wishlist:', error);
        toast.error(language === 'ar' ? 'حدث خطأ في تحميل المفضلة' : 'Error loading wishlist');
        return;
      }

      if (!data) {
        setWishlistItems([]);
        return;
      }

      // Fetch owner details separately for each listing
      const transformedData = await Promise.all(
        data.map(async (item) => {
          if (!item.listing) return null;
          
          // Fetch owner profile
          const { data: ownerData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', item.listing.owner_id)
            .single();

          return {
            id: item.id,
            created_at: item.created_at,
            listing: {
              ...item.listing,
              owner: {
                full_name: ownerData?.full_name || 'Unknown'
              }
            }
          };
        })
      );

      // Filter out null items
      const validItems = transformedData.filter(item => item !== null) as WishlistItem[];
      setWishlistItems(validItems);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error(language === 'ar' ? 'حدث خطأ في تحميل المفضلة' : 'Error loading wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (wishlistId: string) => {
    try {
      const { error } = await supabase
        .from('wishlists')
        .delete()
        .eq('id', wishlistId);

      if (error) {
        console.error('Error removing from wishlist:', error);
        toast.error(language === 'ar' ? 'حدث خطأ في إزالة العقار' : 'Error removing property');
        return;
      }

      setWishlistItems(prev => prev.filter(item => item.id !== wishlistId));
      toast.success(language === 'ar' ? 'تم إزالة العقار من المفضلة' : 'Removed from wishlist');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error(language === 'ar' ? 'حدث خطأ في إزالة العقار' : 'Error removing property');
    }
  };

  useEffect(() => {
    fetchWishlistItems();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleBackClick}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500" />
            <h1 className="text-2xl font-bold">
              {t('wishlist', 'المفضلة', 'Wishlist')}
            </h1>
            {wishlistItems.length > 0 && (
              <span className="text-sm text-muted-foreground">
                ({wishlistItems.length} {language === 'ar' ? 'عقار' : 'properties'})
              </span>
            )}
          </div>
        </div>

        {wishlistItems.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent className="space-y-4">
              <Heart className="h-16 w-16 text-muted-foreground mx-auto" />
              <h2 className="text-xl text-muted-foreground">
                {t('empty_wishlist', 'قائمة المفضلة فارغة', 'Your wishlist is empty')}
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {t(
                  'empty_wishlist_desc', 
                  'ابدأ في إضافة العقارات المفضلة لديك لتسهيل العثور عليها لاحقاً',
                  'Start adding your favorite properties to easily find them later'
                )}
              </p>
              <Button 
                onClick={() => navigate('/search')}
                className="mt-4"
              >
                <Home className="h-4 w-4 mr-2" />
                {t('browse_listings', 'تصفح العقارات', 'Browse Listings')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlistItems.map((item) => (
              <div key={item.id} className="relative group">
                <ListingCard
                  currentLang={language}
                  listing={{
                    id: item.listing.id,
                    title: item.listing.title,
                    titleAr: item.listing.title, // Assuming title is in Arabic if needed
                    price: item.listing.price_egp,
                    deposit: item.listing.deposit_egp,
                    university: language === 'ar' ? item.listing.university.name_ar : item.listing.university.name_en,
                    distance: item.listing.distance_km,
                    furnished: item.listing.furnished,
                    verified: item.listing.verified,
                    images: item.listing.photos,
                    unitType: item.listing.unit_type as 'room' | 'shared' | 'entire',
                    genderRules: item.listing.gender_rules as 'mixed' | 'female' | 'male',
                    responseRate: 95, // Default response rate
                    ownerName: item.listing.owner.full_name || 'Unknown'
                  }}
                />
                
                {/* Remove button overlay */}
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 left-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromWishlist(item.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default Wishlist;
